/**
 * InfinitSales - Widgets do site: tela de conexoes (cards), 5o canal.
 *
 * Analogo da tela "Conexoes" do WhatsApp QR (N instancias), com o minimo na
 * tela: cada card tem nome, status numa palavra, o botao que abre o codigo
 * e o menu "..." (abrir conversas, editar, duplicar, gerar chaves,
 * desligar/religar). Criar pede so nome, site e de qual widget copiar a
 * aparencia; o resto fica no Editar. Cores do tema, como nos outros inboxes;
 * a cor do canal so no icone do titulo e do estado vazio. Os modais moram
 * em components/widget/.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Copy, CopyPlus, ExternalLink, Globe, Loader2, MessageCircle, MoreHorizontal, Pencil, Plus, Power, RefreshCw,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import NavSidebar, { publishWidgetStatus } from '../../components/NavSidebar';
import { useInboxLayout } from '../../components/inbox/InboxLayout';
import ActionMenu, { IconButton } from '../../components/common/ActionMenu';
import { ErrorBox, WidgetUiStyles, errorMessage, formStyles } from '../../components/widget/WidgetModalShell';
import WidgetSnippetModal from '../../components/widget/WidgetSnippetModal';
import WidgetCreateModal from '../../components/widget/WidgetCreateModal';
import WidgetEditModal from '../../components/widget/WidgetEditModal';
import widgetService from '../../services/widget/widgetService';
import { colorOf } from '../../utils/channels';

// Status numa palavra: desligado vence; senao, instalado ou aguardando.
const statusOf = (acc) => {
  if (acc.connection_status === 'disconnected') return { text: 'Desligado', color: '#9ca3af' };
  if (acc.install_status === 'installed') return { text: 'Instalado', color: '#10b981' };
  return { text: 'Aguardando', color: '#f59e0b' };
};

// Largura da janela. Nao ha hook compartilhado (InboxLayout e NavSidebar
// fazem o mesmo em linha); o useViewport da Central de Ajuda traria o
// conteudo dela para o bundle principal.
const useWindowWidth = () => {
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
};

// Deixa o menu "..." fechar antes do window.confirm.
const afterMenu = (fn) => () => { setTimeout(fn, 0); };

const WidgetConnectionsPage = () => {
  const { colors, theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { layoutOwnsNav, setNavProps } = useInboxLayout();
  const width = useWindowWidth();
  const navMobile = width < 768; // NavSidebar vira barra fixa: 54 px em cima, 58 px embaixo
  const narrow = width < 640;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'own' cria/edita; 'mirror' so le; null ate carregar
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [snippetAcc, setSnippetAcc] = useState(null);
  const [createDraft, setCreateDraft] = useState(null); // { sourceId, name }
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await widgetService.listAccounts();
      setAccounts(res?.data || []);
      setMode(res?.mode || 'own');
      setError(null);
    } catch (e) {
      if (e?.response?.status === 404) setError('O canal Widget do site não está habilitado neste ambiente.');
      else setError('Não foi possível carregar os widgets.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshStatus = async () => {
    try {
      const st = await widgetService.getStatus({ force: true });
      publishWidgetStatus(st?.data);
    } catch { /* ignore */ }
  };

  // Gerar novas chaves: so o segredo; o codigo colado no site nao muda.
  // Devolve false quando o confirm e cancelado; erro de API sobe para quem chamou.
  const rotate = async (acc) => {
    if (!window.confirm('Gerar novas chaves? Os visitantes com conversa aberta recomeçam a conversa.')) return false;
    setBusy(acc.id);
    try {
      await widgetService.rotateKeys(acc.id, false);
      await load();
      return true;
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (acc) => {
    const off = acc.connection_status !== 'disconnected';
    if (off && !window.confirm('Desligar este widget? O chat some do site até você religar.')) return false;
    setBusy(acc.id);
    try {
      if (off) await widgetService.disconnect(acc.id, false);
      else await widgetService.updateAccount(acc.id, { connection_status: 'connected' });
      await load();
      await refreshStatus();
      return true;
    } finally {
      setBusy(null);
    }
  };

  // Acao pelo menu do card: o erro vira aviso no topo da tela.
  const fromMenu = (fn, acc) => afterMenu(async () => {
    setActionError(null);
    try {
      await fn(acc);
    } catch (err) {
      setActionError(errorMessage(err, 'Não foi possível concluir a ação.'));
    }
  });

  // Novo: aparencia Padrao; "Igual a..." so por escolha explicita (a lista sao
  // os widgets deste workspace). Duplicar: a do widget e o nome "(copia)".
  const openCreate = (source = null) => setCreateDraft({
    sourceId: source ? source.id : '',
    name: source ? `${String(source.name || 'Widget').slice(0, 72)} (cópia)` : '',
  });

  // Criou: fecha, abre direto o codigo do widget novo, recarrega e avisa a NavSidebar.
  const handleCreated = async (created) => {
    setCreateDraft(null);
    if (created?.snippet) setSnippetAcc(created);
    await load();
    await refreshStatus();
  };

  const handleSaved = async () => {
    setEditingId(null);
    await load();
    await refreshStatus();
  };

  const menuItems = (acc) => {
    const items = [
      { key: 'open', label: 'Abrir conversas', icon: MessageCircle, onClick: () => navigate(`/widget?widget_id=${acc.id}`) },
    ];
    if (acc.mode === 'standalone' && acc.snippet?.standalone_url) {
      items.push({
        key: 'page', label: 'Abrir página de conversa', icon: ExternalLink,
        onClick: () => window.open(acc.snippet.standalone_url, '_blank', 'noopener,noreferrer'),
      });
    }
    if (mode !== 'own') return items;
    const isBusy = busy === acc.id;
    const off = acc.connection_status === 'disconnected';
    items.push(
      { divider: true },
      { key: 'edit', label: 'Editar', icon: Pencil, onClick: () => setEditingId(acc.id) },
      { key: 'dup', label: 'Duplicar', icon: CopyPlus, onClick: () => openCreate(acc) },
      { key: 'rotate', label: 'Gerar novas chaves', icon: RefreshCw, disabled: isBusy, onClick: fromMenu(rotate, acc) },
      { divider: true },
      off
        ? { key: 'on', label: 'Religar', icon: Power, disabled: isBusy, onClick: fromMenu(toggle, acc) }
        : { key: 'off', label: 'Desligar', icon: Power, danger: true, disabled: isBusy, onClick: fromMenu(toggle, acc) },
    );
    return items;
  };

  const navProps = {
    currentPage: 'widget',
    onPageChange: (page) => {
      if (page === 'inbox') { navigate(user?.default_channel === 'wa_business' ? '/wa-business' : '/inbox'); return; }
      navigate(user?.default_channel === 'wa_business' ? '/wa-business' : '/inbox', { state: { targetPage: page } });
    },
    onOpenConnection: () => navigate(user?.default_channel === 'wa_business' ? '/wa-business' : '/inbox', { state: { openConnection: true } }),
  };
  useEffect(() => { if (layoutOwnsNav) setNavProps(navProps); });

  const s = formStyles(colors);
  const canManage = mode === 'own';
  const channelColor = colorOf('widget');
  const editing = editingId ? accounts.find((a) => a.id === editingId) : null;
  const fullHeight = navMobile ? '100dvh' : '100vh';
  const spin = { animation: 'spin 1s linear infinite', flexShrink: 0 };
  const primarySmall = { ...s.primaryBtn, width: 'auto', padding: '9px 16px', fontSize: 13, borderRadius: 8 };
  const card = {
    backgroundColor: colors.bgPrimary, border: `1px solid ${colors.borderColor}`, borderRadius: 12,
    padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, boxShadow: colors.shadowSm,
  };

  return (
    <div style={{
      display: 'flex', overflow: 'hidden', backgroundColor: colors.bgSecondary,
      colorScheme: theme === 'dark' ? 'dark' : 'light',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      ...(layoutOwnsNav
        ? { flex: 1, minWidth: 0, height: fullHeight }
        : { height: fullHeight, width: navMobile ? '100%' : '100vw' }),
    }}>
      {!layoutOwnsNav && <NavSidebar currentPage="widget" onPageChange={navProps.onPageChange} onOpenConnection={navProps.onOpenConnection} />}

      <div style={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
        paddingTop: navMobile ? 54 : 0, paddingBottom: navMobile ? 58 : 0,
      }}>
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
          padding: narrow ? '20px 16px 32px' : '28px 32px 40px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10, fontSize: narrow ? 20 : 22, fontWeight: 600, color: colors.textPrimary }}>
                <Globe size={22} color={channelColor} style={{ flexShrink: 0 }} /> Widgets do site
              </h1>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: colors.textSecondary }}>
                As conversas do chat do seu site chegam no inbox Widget.
              </p>
            </div>
            {canManage && (
              <button type="button" className="wdg-btn" style={primarySmall} onClick={() => openCreate()}>
                <Plus size={15} /> Novo widget
              </button>
            )}
          </div>

          {(error || actionError) && <ErrorBox>{error || actionError}</ErrorBox>}

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: colors.textSecondary }}>
              <Loader2 size={16} style={spin} /> Carregando...
            </div>
          ) : !accounts.length ? (
            !error && (
              <div style={{ ...card, alignItems: 'center', textAlign: 'center', gap: 8, padding: '40px 24px' }}>
                <Globe size={40} color={channelColor} style={{ opacity: 0.6 }} />
                <span style={{ marginTop: 4, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>Nenhum widget ainda</span>
                <span style={{ fontSize: 13, color: colors.textSecondary }}>Crie o primeiro e cole o código no seu site.</span>
                {canManage && (
                  <button type="button" className="wdg-btn" style={{ ...primarySmall, marginTop: 12 }} onClick={() => openCreate()}>
                    <Plus size={15} /> Criar widget
                  </button>
                )}
              </div>
            )
          ) : (
            <div style={{
              display: 'grid', gap: narrow ? 12 : 16,
              gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))',
            }}>
              {accounts.map((acc) => {
                const st = statusOf(acc);
                return (
                  <div key={acc.id} style={card}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span title={acc.name} style={{
                        flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {acc.name || 'Widget'}
                      </span>
                      {busy === acc.id && <Loader2 size={14} color={colors.textSecondary} style={spin} />}
                      <span style={{
                        flexShrink: 0, fontSize: 11, fontWeight: 700, color: st.color, backgroundColor: `${st.color}1f`,
                        padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
                      }}>
                        {st.text}
                      </span>
                      <ActionMenu
                        colors={colors}
                        width={220}
                        items={menuItems(acc)}
                        trigger={({ open }) => (
                          <IconButton
                            icon={MoreHorizontal}
                            label="Mais opções"
                            colors={colors}
                            className="wdg-btn"
                            aria-haspopup="menu"
                            aria-expanded={open}
                          />
                        )}
                      />
                    </div>
                    <button type="button" className="wdg-btn" style={{ ...primarySmall, alignSelf: 'flex-start' }} onClick={() => setSnippetAcc(acc)}>
                      <Copy size={15} /> Copiar código
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {snippetAcc && <WidgetSnippetModal account={snippetAcc} onClose={() => setSnippetAcc(null)} />}
      {createDraft && (
        <WidgetCreateModal
          accounts={accounts}
          defaultSourceId={createDraft.sourceId}
          defaultName={createDraft.name}
          onClose={() => setCreateDraft(null)}
          onCreated={handleCreated}
        />
      )}
      {editing && (
        <WidgetEditModal
          key={editing.id}
          initial={editing}
          onClose={() => setEditingId(null)}
          onSaved={handleSaved}
          onRotate={rotate}
          onToggle={toggle}
        />
      )}
      <WidgetUiStyles colors={colors} />
    </div>
  );
};

export default WidgetConnectionsPage;
